import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Spinner, Form, Button, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import {
  FaTrash,
  FaDollarSign,
  FaShoppingBag,
  FaReceipt,
  FaClock,
  FaTruck,
  FaBoxes,
  FaArrowRight,
} from 'react-icons/fa';
import ApiService from '../../services/ApiService';
import { formatPrecio } from '../../utils/formatters';
import './Admin.css';

const formatDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getPedidoFecha = (pedido) => {
  const raw = pedido?.fechaCreacion || pedido?.createdAt || pedido?.fecha;
  const d = raw ? new Date(raw) : null;
  return d && !Number.isNaN(d.getTime()) ? d : null;
};

const getEstadoClass = (estado) => {
  switch (estado) {
    case 'CREADO':
      return 'admin-dash-estado--creado';
    case 'COBRADO':
      return 'admin-dash-estado--cobrado';
    case 'ENTREGADO':
      return 'admin-dash-estado--entregado';
    case 'CANCELADO':
      return 'admin-dash-estado--cancelado';
    case 'RECHAZADO':
      return 'admin-dash-estado--rechazado';
    default:
      return '';
  }
};

function Admin() {
  const [totalVentas, setTotalVentas] = useState(0);
  const [cantidadPedidosPeriodo, setCantidadPedidosPeriodo] = useState(0);
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    setDateFrom(formatDateInput(firstDay));
    setDateTo(formatDateInput(lastDay));
  }, []);

  useEffect(() => {
    if (!dateFrom || !dateTo) return;

    let cancelled = false;

    const loadDashboard = async () => {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({ desde: dateFrom, hasta: dateTo });
        const [ventasRes, pedidosRes] = await Promise.all([
          ApiService.get(`/pedidos/ventas/total?${params.toString()}`),
          ApiService.get('/pedidos'),
        ]);

        if (cancelled) return;

        const ventasData = ventasRes?.data || ventasRes;
        setTotalVentas(Number(ventasData?.totalVentas || 0));
        setCantidadPedidosPeriodo(Number(ventasData?.cantidadPedidos || 0));
        setPedidos(Array.isArray(pedidosRes?.data) ? pedidosRes.data : []);
      } catch (err) {
        if (cancelled) return;
        console.error('Error al cargar dashboard:', err);
        setError(err?.message || 'Error al cargar el dashboard');
        setTotalVentas(0);
        setCantidadPedidosPeriodo(0);
        setPedidos([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadDashboard();
    return () => {
      cancelled = true;
    };
  }, [dateFrom, dateTo]);

  const ticketPromedio =
    cantidadPedidosPeriodo > 0 ? totalVentas / cantidadPedidosPeriodo : 0;

  const pedidosPendientes = useMemo(
    () => pedidos.filter((p) => p.estado === 'CREADO'),
    [pedidos]
  );

  const pedidosPorEntregar = useMemo(
    () => pedidos.filter((p) => p.estado === 'COBRADO'),
    [pedidos]
  );

  const conteoEstados = useMemo(() => {
    const base = {
      CREADO: 0,
      COBRADO: 0,
      ENTREGADO: 0,
      CANCELADO: 0,
      RECHAZADO: 0,
    };
    for (const p of pedidos) {
      if (base[p.estado] !== undefined) base[p.estado] += 1;
    }
    return base;
  }, [pedidos]);

  const pedidosRecientes = useMemo(() => {
    return [...pedidos]
      .sort((a, b) => {
        const fa = getPedidoFecha(a)?.getTime() || 0;
        const fb = getPedidoFecha(b)?.getTime() || 0;
        return fb - fa;
      })
      .slice(0, 6);
  }, [pedidos]);

  const resetFechasMes = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    setDateFrom(formatDateInput(firstDay));
    setDateTo(formatDateInput(lastDay));
  };

  return (
    <div className="admin-page admin-dashboard">
      <div className="admin-dash-toolbar">
        <div>
          <h3 className="admin-dash-title">Dashboard</h3>
          <p className="admin-dash-subtitle">
            Resumen operativo del período seleccionado
          </p>
        </div>
        <div className="admin-dash-filters">
          <label className="admin-dash-filter-label" htmlFor="dash-desde">
            Desde
          </label>
          <Form.Control
            id="dash-desde"
            type="date"
            size="sm"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            aria-label="Desde"
          />
          <label className="admin-dash-filter-label" htmlFor="dash-hasta">
            Hasta
          </label>
          <Form.Control
            id="dash-hasta"
            type="date"
            size="sm"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            aria-label="Hasta"
          />
          <Button
            variant="link"
            size="sm"
            className="admin-dash-reset"
            onClick={resetFechasMes}
            aria-label="Restablecer al mes actual"
          >
            <FaTrash aria-hidden="true" />
            Mes actual
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <Row className="g-3 admin-dash-kpis">
        <Col xs={12} sm={6} xl={4}>
          <article className="admin-dash-card admin-dash-card--sales">
            <div className="admin-dash-card-icon" aria-hidden="true">
              <FaDollarSign />
            </div>
            <div className="admin-dash-card-body">
              <h4>Total de ventas</h4>
              {loading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <>
                  <p className="admin-dash-metric">{formatPrecio(totalVentas, '$0')}</p>
                  <span className="admin-dash-meta">Cobrado + entregado en el período</span>
                </>
              )}
            </div>
          </article>
        </Col>

        <Col xs={12} sm={6} xl={4}>
          <article className="admin-dash-card admin-dash-card--orders">
            <div className="admin-dash-card-icon" aria-hidden="true">
              <FaShoppingBag />
            </div>
            <div className="admin-dash-card-body">
              <h4>Pedidos cobrados</h4>
              {loading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <>
                  <p className="admin-dash-metric">{cantidadPedidosPeriodo}</p>
                  <span className="admin-dash-meta">En el rango de fechas</span>
                </>
              )}
            </div>
          </article>
        </Col>

        <Col xs={12} sm={6} xl={4}>
          <article className="admin-dash-card admin-dash-card--ticket">
            <div className="admin-dash-card-icon" aria-hidden="true">
              <FaReceipt />
            </div>
            <div className="admin-dash-card-body">
              <h4>Ticket promedio</h4>
              {loading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <>
                  <p className="admin-dash-metric">{formatPrecio(ticketPromedio, '$0')}</p>
                  <span className="admin-dash-meta">Por pedido cobrado</span>
                </>
              )}
            </div>
          </article>
        </Col>

        <Col xs={12} sm={6} xl={6}>
          <article className="admin-dash-card admin-dash-card--pending">
            <div className="admin-dash-card-icon" aria-hidden="true">
              <FaClock />
            </div>
            <div className="admin-dash-card-body">
              <h4>Pendientes de pago</h4>
              {loading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <>
                  <p className="admin-dash-metric">{pedidosPendientes.length}</p>
                  <span className="admin-dash-meta">Estado CREADO · requieren seguimiento</span>
                </>
              )}
            </div>
          </article>
        </Col>

        <Col xs={12} sm={6} xl={6}>
          <article className="admin-dash-card admin-dash-card--fulfill">
            <div className="admin-dash-card-icon" aria-hidden="true">
              <FaTruck />
            </div>
            <div className="admin-dash-card-body">
              <h4>Por entregar</h4>
              {loading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <>
                  <p className="admin-dash-metric">{pedidosPorEntregar.length}</p>
                  <span className="admin-dash-meta">Cobrados listos para despacho</span>
                </>
              )}
            </div>
          </article>
        </Col>
      </Row>

      <Row className="g-3 mt-1">
        <Col xs={12} lg={4}>
          <section className="admin-dash-panel">
            <header className="admin-dash-panel-header">
              <h3>
                <FaBoxes aria-hidden="true" /> Estados actuales
              </h3>
            </header>
            <ul className="admin-dash-estados">
              {Object.entries(conteoEstados).map(([estado, count]) => (
                <li key={estado}>
                  <span className={`admin-dash-estado ${getEstadoClass(estado)}`}>
                    {estado}
                  </span>
                  <strong>{loading ? '—' : count}</strong>
                </li>
              ))}
            </ul>
            <Link to="/pedidos" className="admin-dash-link">
              Ver pedidos <FaArrowRight aria-hidden="true" />
            </Link>
          </section>
        </Col>

        <Col xs={12} lg={8}>
          <section className="admin-dash-panel">
            <header className="admin-dash-panel-header">
              <h3>Últimos pedidos</h3>
              <Link to="/pedidos" className="admin-dash-link admin-dash-link--inline">
                Ver todos <FaArrowRight aria-hidden="true" />
              </Link>
            </header>
            {loading ? (
              <div className="admin-dash-loading">
                <Spinner animation="border" size="sm" />
              </div>
            ) : pedidosRecientes.length === 0 ? (
              <p className="admin-dash-empty">No hay pedidos para mostrar.</p>
            ) : (
              <div className="admin-dash-table-wrap">
                <table className="admin-dash-table">
                  <thead>
                    <tr>
                      <th>Cliente</th>
                      <th>Fecha</th>
                      <th>Estado</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pedidosRecientes.map((pedido) => {
                      const fecha = getPedidoFecha(pedido);
                      const total = pedido.montoNeto ?? pedido.montoTotal ?? 0;
                      return (
                        <tr key={pedido._id || pedido.id}>
                          <td>
                            <span className="admin-dash-cliente">
                              {pedido.nombreContacto || 'Sin nombre'}
                            </span>
                            {pedido.telefonoContacto && (
                              <span className="admin-dash-tel">{pedido.telefonoContacto}</span>
                            )}
                          </td>
                          <td>
                            {fecha
                              ? fecha.toLocaleDateString('es-AR', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric',
                                })
                              : '—'}
                          </td>
                          <td>
                            <span
                              className={`admin-dash-estado ${getEstadoClass(pedido.estado)}`}
                            >
                              {pedido.estado || '—'}
                            </span>
                          </td>
                          <td className="admin-dash-total">{formatPrecio(total)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </Col>
      </Row>
    </div>
  );
}

export default Admin;
